import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { useCallback, useState } from "react";

import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export type SortDir = "asc" | "desc";

/**
 * Sort state for a server-side-sorted table. Clicking the active column flips
 * the direction; clicking a new column selects it ascending. `sortBy` is `null`
 * until the user picks a column, so an unsorted table keeps the RPC's order.
 */
export function useTableSort(initial?: { sortBy: string; sortDir?: SortDir }) {
  const [sortBy, setSortBy] = useState<string | null>(initial?.sortBy ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initial?.sortDir ?? "asc");

  const onSort = useCallback(
    (column: string) => {
      if (sortBy === column) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(column);
        setSortDir("asc");
      }
    },
    [sortBy],
  );

  return { sortBy, sortDir, onSort };
}

interface SortableTableHeadProps {
  /** The backend sort key this header maps to (sent as `sort_by`). */
  column: string;
  label: string;
  /** The column currently sorted on, or `null` when the table is unsorted. */
  sortBy: string | null;
  sortDir: SortDir;
  onSort: (column: string) => void;
  align?: "left" | "right";
  className?: string;
}

/**
 * A clickable table header that drives server-side sorting: clicking calls
 * `onSort(column)`, which the parent turns into new query params. The arrow
 * reflects the active direction; inactive columns show a neutral hint.
 */
export function SortableTableHead({
  column,
  label,
  sortBy,
  sortDir,
  onSort,
  align = "left",
  className,
}: SortableTableHeadProps) {
  const active = sortBy === column;
  return (
    <TableHead className={cn(align === "right" && "text-right", className)}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "hover:text-primary inline-flex items-center gap-1",
          align === "right" && "flex-row-reverse",
        )}
      >
        {label}
        {active ? (
          sortDir === "asc" ? (
            <ArrowUp className="size-3" />
          ) : (
            <ArrowDown className="size-3" />
          )
        ) : (
          <ChevronsUpDown className="text-muted size-3" />
        )}
      </button>
    </TableHead>
  );
}
